import { useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import type { Topic } from '../../types'

type Change = { url: string; data: Record<string, any>; apply: (rows: Topic[]) => Topic[] }

// Rebase waiting operations on accepted server rows, not on snapshots that may
// contain a rejected edit. Never let a heartbeat overwrite pending changes.
export default function useTopicActions(topics: Topic[], baseUrl: string, filters?: object) {
  const [rows, setRows] = useState(topics)
  const [error, setError] = useState('')
  const confirmed = useRef(topics)
  const queue = useRef<Change[]>([])
  const alive = useRef(true)
  const manual = useRef(false)
  const publish = () => setRows(queue.current.reduce((r, op) => op.apply(r), confirmed.current))
  useEffect(() => {
    if (!queue.current.length) { confirmed.current = topics; setRows(topics) }
  }, [topics])
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false; queue.current = [] }
  }, [])

  const pump = () => {
    const op = queue.current[0]
    if (!op || !alive.current) return
    let success = false
    let cancelled = false
    router.put(op.url, op.data, {
      preserveState: true, preserveScroll: true,
      onSuccess: (page) => {
        success = true
        confirmed.current = Array.isArray(page.props.topics) ? page.props.topics as Topic[] : op.apply(confirmed.current)
      },
      onError: (errors) => setError(Object.values(errors).join(', ') || 'Could not save topics.'),
      onCancel: () => { cancelled = true },
      onFinish: () => {
        if (!alive.current) return
        queue.current.shift()
        if (!success) setError('Could not save topics. Changes were reconciled; please try again.')
        if (cancelled) queue.current = []
        publish()
        if (!cancelled) pump()
      }
    })
  }
  const enqueue = (op: Change) => {
    setError('')
    queue.current.push(op)
    publish()
    if (queue.current.length === 1) pump()
  }
  const priority = (topic: Topic, value: boolean) => enqueue({
    url: `${baseUrl}/${topic.id}`, data: { topic: { priority: value }, ...(filters ? { filters: { ...filters, ...(manual.current ? { sort: 'manual' } : {}) } } : {}) },
    apply: rows => rows.map(t => t.id === topic.id ? { ...t, priority: value } : t)
  })
  const reorder = (next: Topic[], extra: Record<string, any> = {}) => {
    manual.current = true
    const ids = next.map(t => t.id)
    enqueue({
      url: `${baseUrl}/reorder`, data: { topic_ids: ids, ...extra, ...(filters ? { filters: { ...filters, sort: 'manual' } } : {}) },
      apply: rows => {
        const byId = new Map(rows.map(t => [t.id, t]))
        const ordered = ids.flatMap(id => {
          const t = byId.get(id)
          return t ? [{ ...t, ...(extra.topic_id === id && extra.destination_type ? { topic_type: extra.destination_type } : {}) }] : []
        })
        return [...ordered, ...rows.filter(t => !ids.includes(t.id))].map((t, position) => ({ ...t, position }))
      }
    })
  }
  const edge = (topic: Topic, edge: 'top' | 'bottom') => {
    manual.current = true
    enqueue({
      url: `${baseUrl}/reorder`, data: { topic_id: topic.id, edge, ...(filters ? { filters: { ...filters, sort: 'manual' } } : {}) },
      apply: rows => {
        const section = rows.filter(t => t.topic_type === topic.topic_type && t.id !== topic.id)
        const current = rows.find(t => t.id === topic.id)
        if (!current) return rows
        edge === 'top' ? section.unshift(current) : section.push(current)
        return rows.map(t => t.topic_type === topic.topic_type ? section.shift()! : t).map((t, position) => ({ ...t, position }))
      }
    })
  }
  return { rows, error, priority, reorder, edge }
}
